import { Box, BoxProps, HStack, Stack } from '@chakra-ui/react';
import { IconButton } from 'Atoms';
import { BackToTopButton } from 'Atoms/BackToTopButton/BackToTopButton';
import CompanySwitcher from 'Features/CompanySwitcher/CompanySwitcher';
import { ContentHeader, Loader } from 'Molecules';
import { Breadcrumbs } from 'Molecules/Breadcrumbs';
import { useAuth } from 'Providers/auth/AuthProvider';
import React, { Suspense, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type ContentLayoutProps = {
  children?: React.ReactNode | React.ReactNode[];
  isLoading?: boolean;
  loadingText?: string;
  header: string | React.ReactNode;
  contentProps?: BoxProps;
  height?: string;
  contentHeight?: string;
  variant?: 'wide' | 'narrow' | 'inline' | 'inline.nopad';
  paddingTop?: string;
  paddingBottom?: string;
  backToTop?: boolean;
  backButton?: boolean | React.ReactNode;
  onBackButtonClick?: () => void;
  onBackNavigate?: () => void;
  actions?: React.ReactNode;
  noPadding?: boolean;
  relativeContent?: React.ReactNode;
  noHeader?: boolean;
};

export const LG_PADDING_HEIGHT = '56px';
export const MD_PADDING_HEIGHT = '32px';
export const SM_PADDING_HEIGHT = '16px';
export const TOP_MENU_HEIGHT = '48px';

export type BackButtonProps = {
  onClick?: () => void;
};
export const BackButton = ({ onClick }: BackButtonProps) => {
  const navigate = useNavigate();
  const defaultOnClick = () => navigate(-1);
  const navigateOnClick = onClick || defaultOnClick;
  return <IconButton size="md" variant="ghost" onClick={navigateOnClick} aria-label={'Back'} />;
};

export const ContentLayout = ({
  children,
  isLoading = false,
  loadingText,
  header,
  contentProps = {},
  backButton,
  onBackButtonClick,
  onBackNavigate,
  backToTop,
  variant = 'wide',
  paddingTop, // paddingTop & paddingBottom are added to make it possible to override default ->
  paddingBottom, // -> padding values locally. Add !important if override doesn't work on all screen sizes.
  actions,
  noPadding,
  relativeContent,
  noHeader,
}: ContentLayoutProps) => {
  let head;
  if (typeof header === 'string') {
    head = <ContentHeader title={header} />;
  } else {
    head = header;
  }

  let variantStyles;
  switch (variant) {
    case 'wide':
      variantStyles = {
        maxWidth: '1560px',
        minWidth: '800px',
        padding: ['16px', '16px', '16px', '16px', '40px'], // Side paddings
        paddingTop: [
          SM_PADDING_HEIGHT,
          SM_PADDING_HEIGHT,
          MD_PADDING_HEIGHT,
          MD_PADDING_HEIGHT,
          MD_PADDING_HEIGHT,
        ],
        paddingBottom: [
          SM_PADDING_HEIGHT,
          SM_PADDING_HEIGHT,
          SM_PADDING_HEIGHT,
          LG_PADDING_HEIGHT,
          LG_PADDING_HEIGHT,
        ],
      };
      break;
    case 'narrow':
      variantStyles = {
        maxWidth: '840px',
        minWidth: '800px',
        padding: ['16px', '16px', '16px', '16px', '16px'], // Side paddings
        paddingTop: [
          SM_PADDING_HEIGHT,
          SM_PADDING_HEIGHT,
          MD_PADDING_HEIGHT,
          LG_PADDING_HEIGHT,
          LG_PADDING_HEIGHT,
        ],
        paddingBottom: [
          LG_PADDING_HEIGHT,
          LG_PADDING_HEIGHT,
          LG_PADDING_HEIGHT,
          LG_PADDING_HEIGHT,
          LG_PADDING_HEIGHT,
        ],
      };
      break;
    case 'inline':
      variantStyles = {
        width: '100%',
        maxWidth: '1560px',
        minWidth: '800px',
        padding: '16px', // Side paddings
        paddingTop: SM_PADDING_HEIGHT,
        paddingBottom: SM_PADDING_HEIGHT,
      };
      break;
    case 'inline.nopad':
      variantStyles = {
        width: '100%',
        maxWidth: '100%',
        padding: '0',
        paddingTop: '0',
        paddingBottom: '0',
      };
  }

  const mergedStyles = {
    ...variantStyles,
    paddingBottom: paddingBottom || variantStyles.paddingBottom,
    paddingTop: paddingTop || variantStyles.paddingTop,
  };
  const [showBackToTop, setShowBackToTop] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  const handleScroll = () => {
    setShowBackToTop((pageRef.current?.scrollTop ?? 0) > 0);
  };

  return (
    <Loader isLoaded={!isLoading} label={loadingText}>
      <Stack
        ref={pageRef}
        width="100%"
        gap="0px"
        alignItems={{ base: 'flex-start', lg: 'center' }}
        onScroll={handleScroll}
        display="flex"
        overflowY="auto"
        flexGrow="1"
      >
        {backToTop && showBackToTop && <BackToTopButton containerRef={pageRef} />}

        {!noHeader && (
          <HStack
            direction="row"
            padding="16px 16px 4px"
            bg="bg.default"
            width="100%"
            minHeight={TOP_MENU_HEIGHT}
            zIndex={3}
            gap="8px"
            alignItems="center"
            position="sticky"
            top="0"
            borderBottom={showBackToTop ? '1px solid' : 'none'}
            borderColor={showBackToTop ? 'border.default' : 'transparent'}
          >
            {user ? <CompanySwitcher user={user} /> : <></>}
            {backButton && (
              <Breadcrumbs
                onBackButtonClick={onBackButtonClick}
                onBackNavigate={onBackNavigate}
                noPadding
              />
            )}
          </HStack>
        )}
        {backButton && (
          <HStack width="100%" justifyContent="space-between" pr="8px">
            {actions}
          </HStack>
        )}
        {relativeContent && (
          <Box position="absolute" height={`calc(100vh - 48px)`}>
            <Box position="relative" height="100%">
              {relativeContent}
            </Box>
          </Box>
        )}
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          flexGrow="1"
          alignSelf={{ base: 'flex-start', lg: 'center' }}
          {...variantStyles}
          {...mergedStyles}
          {...contentProps}
          padding={noPadding ? 0 : variantStyles.padding}
        >
          {head}
          <Suspense>{children}</Suspense>
        </Box>
      </Stack>
    </Loader>
  );
};
